import { useEffect, useMemo, useState } from 'react';

import { PaginationProps } from '../BoardPagination';

export type HandleType = 'next' | 'prev' | 'first' | 'last';

// pagination 생성기
// showPages: 보여질 페이지 수
// pages: 데이터들의 전체 페이지 수
// currentPage: 현재 페이지
export const generatePagination = (
  pages: number,
  currentPage: number,
  showPages: number,
) => {
  return Array.from(
    { length: pages >= showPages ? showPages : pages },
    (_, i) => {
      if (currentPage <= 3 && pages <= showPages) {
        return i + 1;
      } else if (currentPage > 3 && pages > showPages) {
        if (currentPage <= pages - 2) {
          return currentPage - 2 + i;
        } else if (currentPage > pages - 2) {
          return pages - showPages + i + 1;
        }
      }
      return i + 1;
    },
  );
};

const useBoardPagination = ({
  page,
  handlePage,
  totalPages,
  showPages = 5,
  paginationType = 'dynamic',
  value = '',
  isRouterUrlChange = false,
  routerUrl,
}: Omit<PaginationProps, 'hasFirstArrow' | 'hasLastArrow'>) => {
  const [generateStaticPagination, setGenerateStaticPagination] = useState<
    number[] | []
  >([]);
  const [handleType, setHandleType] = useState<'prev' | 'next' | ''>('');

  const generatedPagination = useMemo(
    () =>
      paginationType === 'static'
        ? generateStaticPagination
        : generatePagination(totalPages, page, showPages),
    [page, generateStaticPagination, totalPages, paginationType, showPages],
  );

  const onClickPageNumber = (_page: number) => {
    handlePage && handlePage(_page);
    if (isRouterUrlChange && routerUrl && routerUrl !== '') {
      location.href = `${routerUrl}?page=${_page}`;
    }
  };

  const handlePageRouter = (type: HandleType) => {
    if (type === 'first') {
      handlePage && handlePage(1);
      if (isRouterUrlChange && routerUrl && routerUrl !== '') {
        location.href = `${routerUrl}?page=${1}`;
      }
      return;
    }

    if (type === 'last') {
      handlePage && handlePage(totalPages);

      if (isRouterUrlChange && routerUrl && routerUrl !== '') {
        location.href = `${routerUrl}?page=${totalPages}`;
      }
      return;
    }

    if (type === 'next') {
      if (page !== totalPages) {
        handlePage && handlePage(page + 1);
        if (isRouterUrlChange && routerUrl && routerUrl !== '') {
          location.href = `${routerUrl}?page=${Number(page) + 1}`;
        }
      }
    } else if (type === 'prev') {
      if (page !== 1) {
        handlePage && handlePage(page - 1);
        if (isRouterUrlChange && routerUrl && routerUrl !== '') {
          location.href = `${routerUrl}?page=${Number(page) - 1}`;
        }
      }
    }
  };

  useEffect(() => {
    if (handleType === 'prev') {
      if (page % 10 === 0) {
        const arr = Array.from({ length: showPages }, (_, i) => page - i);
        const result = [...arr].reverse().filter((v) => v <= totalPages);
        setGenerateStaticPagination(result);
      }
    } else if (handleType === 'next') {
      if (page % 10 === 1) {
        const arr = Array.from({ length: showPages }, (_, i) => page + i);
        const result = arr.filter((v) => v <= totalPages);
        setGenerateStaticPagination(result);
      }
    } else {
      const result = Array.from({ length: showPages }, (_, i) => i + 1).filter(
        (v) => v <= totalPages,
      );
      setGenerateStaticPagination(result);
    }
  }, [handleType, page, totalPages, showPages, setGenerateStaticPagination]);

  return {
    setHandleType,
    generatedPagination,
    onClickPageNumber,
    handlePageRouter,
  };
};

export default useBoardPagination;
